import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, User, Clock, Send, Loader2,
  AlertTriangle, MessageSquare, CheckCircle,
  Paperclip, X, FileText, Image, File, Clipboard
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const TenantTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const res = await api.get(`/support/tickets/${ticketId}`);
        setTicket(res.data);
      } catch (err) {
        console.error('Failed to fetch ticket:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [api, ticketId]);

  // Handle paste event for images
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        if (file.size > 5 * 1024 * 1024) {
          alert('Pasted image is too large. Maximum size is 5MB.');
          continue;
        }

        setUploading(true);
        try {
          const reader = new FileReader();
          const dataUrl = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
          setAttachments(prev => [...prev, {
            filename: `pasted-image-${timestamp}.png`,
            url: dataUrl,
            content_type: file.type,
            size: file.size,
            isPasted: true
          }]);
        } catch (err) {
          console.error('Failed to read pasted image:', err);
        } finally {
          setUploading(false);
        }
      }
    }
  }, []);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newAttachments = [];
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }
      
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newAttachments.push({
        filename: file.name,
        url: dataUrl,
        content_type: file.type,
        size: file.size
      });
    }
    
    setAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('pdf') || contentType?.includes('document')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const addComment = async () => {
    if (!newComment.trim() && attachments.length === 0) return;
    setSendingComment(true);
    try {
      await api.post(`/support/tickets/${ticketId}/comments`, {
        content: newComment,
        attachments: attachments.length > 0 ? attachments : null
      });
      // Refresh ticket
      const res = await api.get(`/support/tickets/${ticketId}`);
      setTicket(res.data);
      setNewComment('');
      setAttachments([]);
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSendingComment(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-purple-500/20 text-purple-600 dark:text-purple-400';
      case 'open': return 'bg-blue-500/20 text-blue-600 dark:text-blue-400';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400';
      case 'waiting_on_customer': return 'bg-orange-500/20 text-orange-600 dark:text-orange-400';
      case 'waiting_on_support': return 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400';
      case 'resolved': return 'bg-green-500/20 text-green-600 dark:text-green-400';
      case 'closed': return 'bg-gray-500/20 text-gray-600 dark:text-gray-400';
      default: return 'bg-gray-500/20 text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
      case 'closed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'waiting_on_customer':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'waiting_on_support':
        return <Clock className="h-5 w-5 text-cyan-500" />;
      default:
        return <Clock className="h-5 w-5 text-blue-500" />;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      general: 'General',
      billing: 'Billing',
      technical: 'Technical',
      feature_request: 'Feature Request',
      bug_report: 'Bug Report'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Ticket not found</p>
        <Button variant="ghost" onClick={() => navigate('/support')} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Support
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/support')}
          data-testid="back-to-support-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-primary font-mono font-medium">{ticket.ticket_number}</span>
            <Badge className={getStatusColor(ticket.status)}>
              {ticket.status?.replace(/_/g, ' ')}
            </Badge>
            <Badge variant="outline">{getCategoryLabel(ticket.category)}</Badge>
          </div>
          <h1 className="text-2xl font-bold">{ticket.subject}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" /> {ticket.submitted_by_name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" /> {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusIcon(ticket.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-muted-foreground">{ticket.description}</p>
              
              {/* Ticket Attachments */}
              {ticket.attachments && ticket.attachments.length > 0 && (
                <div className="mt-4 pt-4 border-t space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Attachments:</p>
                  {ticket.attachments.map((file, idx) => {
                    const FileIcon = getFileIcon(file.content_type);
                    const isImage = file.content_type?.startsWith('image/');
                    return (
                      <div key={idx}>
                        {isImage ? (
                          <a 
                            href={file.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img 
                              src={file.url} 
                              alt={file.filename}
                              className="max-w-sm rounded-lg border hover:opacity-90 transition-opacity"
                            />
                          </a>
                        ) : (
                          <a
                            href={file.url}
                            download={file.filename}
                            className="inline-flex items-center gap-2 p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
                          >
                            <FileIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{file.filename}</span>
                            <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" /> Comments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticket.comments?.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground">No comments yet</p>
              ) : (
                <div className="space-y-4">
                  {ticket.comments?.map((comment) => (
                    <div
                      key={comment.id}
                      className={`p-4 rounded-lg ${
                        comment.author_type === 'support'
                          ? 'bg-primary/10 border border-primary/30'
                          : 'bg-secondary'
                      }`}
                      data-testid={`comment-${comment.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{comment.author_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {comment.author_type === 'support' ? 'Support Agent' : 'You'}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(comment.created_at), 'PPp')}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap">{comment.content}</p>
                      
                      {/* Comment Attachments - show inline images */}
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {comment.attachments.map((file, idx) => {
                            const FileIcon = getFileIcon(file.content_type);
                            const isImage = file.content_type?.startsWith('image/');
                            return (
                              <div key={idx}>
                                {isImage ? (
                                  <a 
                                    href={file.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="block"
                                  >
                                    <img 
                                      src={file.url} 
                                      alt={file.filename}
                                      className="max-w-md rounded-lg border hover:opacity-90 transition-opacity"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={file.url}
                                    download={file.filename}
                                    className="inline-flex items-center gap-2 p-2 rounded-lg bg-background hover:bg-background/80 transition-colors border"
                                  >
                                    <FileIcon className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm">{file.filename}</span>
                                    <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Comment - only if ticket not closed */}
              {ticket.status !== 'closed' && (
                <div className="pt-4 border-t">
                  <div className="relative">
                    <Textarea
                      ref={textareaRef}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onPaste={handlePaste}
                      placeholder="Add a comment... (Tip: You can paste screenshots directly!)"
                      className="mb-3 pr-10"
                      rows={4}
                      data-testid="comment-textarea"
                    />
                    <div className="absolute right-3 top-3">
                      <Clipboard className="h-4 w-4 text-muted-foreground" title="Paste images with Ctrl+V" />
                    </div>
                  </div>
                  
                  {/* Attachments Preview - show pasted images inline */}
                  {attachments.length > 0 && (
                    <div className="mb-3 space-y-2">
                      {attachments.map((file, index) => {
                        const FileIcon = getFileIcon(file.content_type);
                        const isImage = file.content_type?.startsWith('image/');
                        return (
                          <div 
                            key={index}
                            className="relative"
                          >
                            {isImage ? (
                              <div className="relative inline-block">
                                <img 
                                  src={file.url} 
                                  alt={file.filename}
                                  className="max-w-xs rounded-lg border"
                                />
                                <button
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 p-1 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                                {file.isPasted && (
                                  <span className="absolute bottom-2 left-2 text-xs bg-black/50 text-white px-2 py-1 rounded">
                                    Pasted image
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-3 p-2 rounded-lg bg-secondary">
                                <FileIcon className="h-5 w-5 text-muted-foreground" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm truncate">{file.filename}</p>
                                  <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                                </div>
                                <button
                                  onClick={() => removeAttachment(index)}
                                  className="p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        className="hidden"
                        multiple
                        accept="image/*,.pdf,.doc,.docx"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="attach-file-btn"
                      >
                        {uploading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Paperclip className="h-4 w-4 mr-2" />
                        )}
                        Attach File
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        or paste an image (Ctrl+V)
                      </span>
                    </div>
                    <Button
                      onClick={addComment}
                      disabled={sendingComment || (!newComment.trim() && attachments.length === 0)}
                      data-testid="send-comment-btn"
                    >
                      {sendingComment ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send
                    </Button>
                  </div>
                </div>
              )}

              {/* Closed ticket notice */}
              {ticket.status === 'closed' && (
                <div className="pt-4 border-t">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted text-muted-foreground">
                    <CheckCircle className="h-5 w-5" />
                    <span>This ticket has been closed. If you need further assistance, please open a new ticket.</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ticket Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusIcon(ticket.status)}
                  <span className="font-medium capitalize">{ticket.status?.replace(/_/g, ' ')}</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Category</p>
                <p className="font-medium mt-1">{getCategoryLabel(ticket.category)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="font-medium mt-1">{format(new Date(ticket.created_at), 'PPP')}</p>
                <p className="text-xs text-muted-foreground">{format(new Date(ticket.created_at), 'p')}</p>
              </div>
              {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
                <div>
                  <p className="text-sm text-muted-foreground">Last Updated</p>
                  <p className="font-medium mt-1">{formatDistanceToNow(new Date(ticket.updated_at), { addSuffix: true })}</p>
                </div>
              )}
              {ticket.resolved_at && (
                <div>
                  <p className="text-sm text-muted-foreground">Resolved</p>
                  <p className="font-medium mt-1">{format(new Date(ticket.resolved_at), 'PPP')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status explanation */}
          {ticket.status === 'waiting_on_customer' && (
            <Card className="border-orange-500/30 bg-orange-500/5">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-600 dark:text-orange-400">Action Required</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Our support team is waiting for your response. Please reply to this ticket to continue.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantTicketDetail;
